import Trash from '@nerdwallet/react-icon/gizmo/Trash';
import Write from '@nerdwallet/react-icon/gizmo/Write';
import ContentCopy from '@nerdwallet/react-icon/material/ContentCopy';
import get from 'lodash/get';
import { usePathname } from 'next/navigation';
import { Fragment, useState } from 'react';
import { useDispatch } from 'react-redux';
import { isInBuilder } from '~/app/lib/helpers';
import { useBuilder } from '~/app/lib/redux/selectors/builder-selector';
import ActionDropdown from '../editor-control/action-dropdown';
import { DynamicAccordion } from './dynamic-components/dynamic-accordion/dynamic-accordion-component';
import { DynamicAdvertiserDisclosureComponent } from './dynamic-components/dynamic-advertiser-disclosure/advertiser-disclosure-component';
import { DynamicBulletList } from './dynamic-components/dynamic-bullet-list/dynamic-bullet-list-component';
import {
  BulletListItem,
  BulletListStyle,
  ElementType,
} from './dynamic-components/dynamic-bullet-list/types';
import { DynamicButton } from './dynamic-components/dynamic-button/dynamic-button-component';
import {
  CustomButtonColorsType,
  CustomButtonIconType,
} from './dynamic-components/dynamic-button/use-dynamic-button-form';
import { DynamicImageComponent } from './dynamic-components/dynamic-image/dynamic-image-component';
import { DynamicLink } from './dynamic-components/dynamic-link/dynamic-link-component';
import { DynamicStarRating } from './dynamic-components/dynamic-star-rating/dynamic-star-rating-component';
import {
  DynamicTextComponent,
  DynamicTextComponentProps,
} from './dynamic-components/dynamic-text/dynamic-text-component';
import { DynamicTooltip } from './dynamic-components/dynamic-tooltip/dynamic-tooltip-component';
import {
  changeOrder,
  openEditor,
  selectComponent,
  selectTab,
  setComponentPath,
  setComponentType,
  updateComponent,
} from './dynamic-grid-helpers';
import { SegmentTrackingProps } from './segment/segment-tracking-form';

import {
  TableColumn,
  TableRow,
  TableStylesType,
} from '../fixed-first-column-table/types';
import { DynamicFixedFirstColumnTable } from './dynamic-components/dynamic-fixed-first-column-table/dynamic-fixed-first-column-table-component';
import { DynamicLottieAnimation } from './dynamic-components/dynamic-lottie-animation/dynamic-lottie-animation-component';
import { DynamicPressReleasesArchive } from './dynamic-components/dynamic-press-releases-archive/dynamic-press-releases-archive';
import { DynamicThirdPartyEmbed } from './dynamic-components/dynamic-third-party-embed/dynamic-third-party-embed-component';
import { DynamicAuthButton } from './dynamic-components/dynamic-auth-button/dynamic-auth-button';

import AutoInsuranceMatches from '../auto-insurance-matches/base-auto-insurance-matches';
import styles from './dynamic-grid.module.scss';

export type ComponentType = {
  $unique_key?: string;
  componentType: string;
  text?: string;
  color?: string;
  hexColor?: string;
  imageUrl?: string;
  imageHeight?: number;
  imageWidth?: number;
  width?: number;
  height?: number;
  objectFit?: string;
  buttonType?: string;
  marketplaceOffer?: string;
  urlSource?: string;
  label?: ComponentType;
  content?: ComponentType;
  showPhoneIcon?: boolean;
  customButtonColors?: CustomButtonColorsType;
  customButtonIcon?: CustomButtonIconType;
  segment?: SegmentTrackingProps;
};

type ImageComponentType = ComponentType & {
  imageUrl?: string;
  width?: number;
  height?: number;
  objectFit?: string;
  circularImage?: boolean;
  imageLinkUrl?: string;
  rolloverImageUrl?: string;
};

type BulletListComponentType = ComponentType & {
  elementType?: ElementType;
  items?: BulletListItem[];
  imageUrl?: string;
  imageHeight?: number;
  imageWidth?: number;
  listStyleType?: BulletListStyle;
  listGap?: number;
  marketplaceOfferId?: string;
  rateAnnotation?: string;
  fontProperties?: {
    fontSize: number;
    tablet?: {
      fontSize: number;
    };
    mobile?: {
      fontSize: number;
    };
  };
};

type TooltipComponentType = ComponentType & {
  label: ComponentType;
  content: ComponentType;
};

type AccordionComponentType = ComponentType & {
  sections: {
    header: ComponentType;
    body: ComponentType;
    footer: ComponentType;
    bodyContentType?: 'text' | 'bulletList';
    bodyBulletList?: ComponentType;
  }[];
};

type TextComponentType = ComponentType & {
  lineHeight?: string;
  letterSpacing?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  textAlign?: string;
  fontSize?: number;
  fontFamily?: string;
};

type ButtonComponentType = ComponentType & {
  ctaLinkOption?: string;
  url?: string;
  authenticatedUrl?: string;
  ctaSubtext: DynamicTextComponentProps;
  ctaExtraText: DynamicTextComponentProps;
  blockButton?: boolean;
};

type LinkComponentType = ComponentType & {
  url?: string;
  urlSource?: string;
  marketplaceOfferId?: string;
  linkOption?: string;
  captureCustomQueryParams?: boolean;
  authenticatedUrl?: string;
  dynamicTextOptions: DynamicTextComponentProps;
};

type StarRatingComponentType = ComponentType & {
  starRating?: number;
  starRatingLayout?: 'stacked' | 'big-number' | 'numeric' | undefined;
  marketplaceOfferId?: string;
  starRatingSource?: string;
};

type AdvertiserDisclosureComponentType = ComponentType & {
  onBackground?: boolean;
  type?: string;
};

type FixedFirstColumnTableComponentType = ComponentType & {
  tableSettings: {
    centeredInParent: boolean;
    tableStyle: TableStylesType;
    transposed: boolean;
    hideHeader: boolean;
    striped: boolean;
  };
  headers?: TableColumn[];
  tableData?: TableRow[];
};

type LottieAnimationComponentType = ComponentType & {
  animationData: string;
  animationMode: string;
  dimensions: {
    width: number;
    height: number;
    tablet?: {
      width?: number;
      height?: number;
    };
    mobile?: {
      width?: number;
      height?: number;
    };
  };
  visibility: {
    x: string;
    y: string;
  };
  frames: {
    x: string;
    y: string;
  };
};

type ThirdPartyEmbedComponentType = ComponentType & {
  embedType?: string;
  iframeSrc?: string;
  scriptSrc?: string;
  cffId?: string;
  height?: string;
  width?: string;
  tabletHeight?: string;
  tabletWidth?: string;
  mobileHeight?: string;
  mobileWidth?: string;
  customCss?: string;
};

type PressReleasesArchiveComponentType = ComponentType & {
  offset?: number;
  limit?: number;
};

type AuthButtonComponentType = ComponentType & {
  authButtonType?: string;
};

export const Component = ({
  component,
  pathContext,
  componentId,
}: {
  component:
    | ComponentType
    | AccordionComponentType
    | AdvertiserDisclosureComponentType
    | BulletListComponentType
    | ButtonComponentType
    | FixedFirstColumnTableComponentType
    | ImageComponentType
    | StarRatingComponentType
    | TooltipComponentType
    | TextComponentType
    | LottieAnimationComponentType
    | ThirdPartyEmbedComponentType
    | PressReleasesArchiveComponentType;
  pathContext: { path: string; index: number };
  componentId: string;
}) => {
  const dispatch = useDispatch();
  const { editor, flatOutline } = useBuilder();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const pathname = usePathname();
  const isBuilderPage = isInBuilder(pathname);

  const getComponentPath = () => {
    const { path, index } = pathContext;
    return `${path}.components.${index}`;
  };

  const handleEditClick = () => {
    const componentPath = getComponentPath();
    dispatch(setComponentPath(componentPath));
    dispatch(setComponentType(component?.componentType));
    dispatch(selectComponent(componentId));

    if (!editor.open) {
      dispatch(openEditor);
    }

    dispatch(selectTab(4));
  };

  const handleCloneClick = () => {
    const componentPath = getComponentPath();
    dispatch(setComponentPath(componentPath));
    dispatch(setComponentType(component?.componentType));
    dispatch(selectComponent(componentId));
    if (component?.$unique_key) {
      const { path } = pathContext;
      // Get the correct parent component using componentId instead of potentially stale selectedComponent
      const selectedDynamicGrid = flatOutline[componentId];
      const parent = get(selectedDynamicGrid, `props.${path}.components`);
      const componentToClone = get(
        selectedDynamicGrid,
        `props.${componentPath}`
      );
      const newComponent = {
        ...componentToClone,
        $unique_key: new Date().getTime(),
      };
      parent.push(newComponent);
      const componentIndex = parseInt(
        componentPath.split('.').pop() || '0',
        10
      );
      const updatedDynamicGrid = changeOrder(
        parent,
        parent.length - 1,
        componentIndex + 1
      );
      dispatch(updateComponent(updatedDynamicGrid));
    }
  };

  const handleDeleteClick = () => {
    const componentPath = getComponentPath();
    dispatch(setComponentPath(componentPath));
    dispatch(setComponentType(component?.componentType));
    dispatch(selectComponent(componentId));
    if (component?.$unique_key) {
      const { path, index } = pathContext;
      // Get the correct parent component using componentId instead of potentially stale selectedComponent
      const selectedDynamicGrid = flatOutline[componentId];
      const parent = get(selectedDynamicGrid, `props.${path}.components`);

      // Remove the component at the specified index
      parent.splice(index, 1);

      dispatch(updateComponent(selectedDynamicGrid));
    }
  };

  const renderComponent = () => {
    const textComponent = component as TextComponentType;
    const accordionComponent = component as AccordionComponentType;
    const bulletListComponent = component as BulletListComponentType;
    const buttonComponent = component as ButtonComponentType;
    const linkComponent = component as LinkComponentType;
    const starRatingComponent = component as StarRatingComponentType;
    const imageComponent = component as ImageComponentType;
    const advertiserDisclosureComponent =
      component as AdvertiserDisclosureComponentType;
    const fixedFirstColumnTableComponent =
      component as FixedFirstColumnTableComponentType;
    const lottieAnimationComponent = component as LottieAnimationComponentType;
    const thirdPartyEmbedComponent = component as ThirdPartyEmbedComponentType;
    const pressReleasesArchiveComponent =
      component as PressReleasesArchiveComponentType;
    const authButtonComponent = component as AuthButtonComponentType;

    switch (component?.componentType) {
      case 'text':
        return <DynamicTextComponent {...textComponent} />;
      case 'image':
        return <DynamicImageComponent {...imageComponent} />;
      case 'bulletList':
        return <DynamicBulletList {...bulletListComponent} />;
      case 'button':
        return <DynamicButton {...buttonComponent} />;
      case 'link':
        return <DynamicLink {...linkComponent} />;
      case 'accordion':
        return <DynamicAccordion {...accordionComponent} />;
      case 'tooltip':
        return (
          <DynamicTooltip
            labelProps={{ ...component?.label }}
            contentProps={{ ...component?.content }}
          />
        );
      case 'star-rating':
        return <DynamicStarRating {...starRatingComponent} />;
      case 'advertiser-disclosure':
        return (
          <DynamicAdvertiserDisclosureComponent
            {...advertiserDisclosureComponent}
          />
        );
      case 'fixed-first-column-table':
        return (
          <DynamicFixedFirstColumnTable {...fixedFirstColumnTableComponent} />
        );
      case 'lottie-animation':
        return <DynamicLottieAnimation {...lottieAnimationComponent} />;
      case 'third-party-embed':
        return <DynamicThirdPartyEmbed {...thirdPartyEmbedComponent} />;
      case 'press-releases-archive':
        return (
          <DynamicPressReleasesArchive {...pressReleasesArchiveComponent} />
        );
      case 'auto-insurance-matches':
        return <AutoInsuranceMatches />;
      case 'auth-button':
        return <DynamicAuthButton {...authButtonComponent} />;
      default:
        return null;
    }
  };

  const isBlockButton =
    component?.componentType === 'button' &&
    (component as ButtonComponentType).blockButton;

  return (
    <Fragment>
      {isBuilderPage ? (
        <div
          id={component?.$unique_key}
          className={styles.componentContainer}
          style={{
            width:
              component?.componentType === 'accordion' || isBlockButton
                ? '100%'
                : 'unset', // workaround to fix builder accordion and button (when blockButton is true) not taking max width
          }}
        >
          {renderComponent()}
          <div
            className={`${styles.componentContainerControls} ${
              isDropdownOpen ? styles.componentContainerControlsVisible : ''
            }`}
          >
            <button
              type="button"
              aria-label="Edit"
              onClick={handleEditClick}
              className={styles.componentContainerEditButton}
            >
              <Write />
            </button>
            <ActionDropdown
              actions={[
                {
                  key: 'clone',
                  label: 'Clone',
                  icon: <ContentCopy />,
                  onClick: handleCloneClick,
                  title: 'Clone Component',
                  show: true,
                },
                {
                  key: 'delete',
                  label: 'Delete',
                  icon: <Trash />,
                  onClick: handleDeleteClick,
                  title: 'Delete Component',
                  danger: true,
                  show: true,
                },
              ]}
              onOpenChange={setIsDropdownOpen}
            />
          </div>
        </div>
      ) : (
        <Fragment>{renderComponent()}</Fragment>
      )}
    </Fragment>
  );
};
